/**
 * "A small look inside": what a CSV or TXT file seems to hold, worked out in the browser from its first
 * 64 KB and shown to the person as a help ("12 columns recognised: ..."). It is local and read-only:
 * nothing is sent, every cell is only ever text, and it is no substitute for the server's analysis.
 * XLSX gets none (it would need a library).
 */
export const PEEK_BYTES = 64 * 1024;

export interface CsvPeek {
  delimiter: string;
  columns: string[];
  /** Rows after the header: counted in the slice, estimated for the whole file when the slice is only a part. */
  rows: number;
  approximate: boolean;
  /** A few values of each column, for the hints. */
  sample: string[][];
}

const CANDIDATES = [";", ",", "\t", "|"];

/** Splits one line at a delimiter, honouring double quotes ("" is a quote inside). */
export function splitLine(line: string, delimiter: string): string[] {
  const cells: string[] = [];
  let cell = "";
  let quoted = false;
  for (let index = 0; index < line.length; index += 1) {
    const char = line[index];
    if (quoted) {
      if (char === '"') {
        if (line[index + 1] === '"') {
          cell += '"';
          index += 1;
        } else quoted = false;
      } else cell += char;
    } else if (char === '"') quoted = true;
    else if (char === delimiter) {
      cells.push(cell);
      cell = "";
    } else cell += char;
  }
  cells.push(cell);
  return cells.map((value) => value.trim());
}

/** The delimiter that splits the first lines into the same number of cells, at least two. */
export function detectDelimiter(lines: string[]): string | null {
  let best: { delimiter: string; score: number } | null = null;
  for (const delimiter of CANDIDATES) {
    const counts = lines.slice(0, 8).map((line) => splitLine(line, delimiter).length);
    const first = counts[0];
    if (!first || first < 2) continue;
    const consistent = counts.filter((count) => count === first).length;
    const score = consistent * 100 + first;
    if (!best || score > best.score) best = { delimiter, score };
  }
  return best?.delimiter ?? null;
}

/** Pure part: the text of the slice and the real size of the file. */
export function peekText(text: string, totalBytes: number, sliceBytes: number): CsvPeek | null {
  const clean = text.replace(/^﻿/, "");
  const lines = clean.split(/\r\n|\r|\n/).filter((line) => line.trim() !== "");
  if (lines.length === 0) return null;
  const delimiter = detectDelimiter(lines);
  if (!delimiter) return null;
  const columns = splitLine(lines[0], delimiter).filter((name, index, all) => name !== "" || index < all.length - 1);
  if (columns.length < 2) return null;
  const partial = totalBytes > sliceBytes;
  // the last line of a partial slice is probably cut off
  const complete = partial ? lines.slice(0, -1) : lines;
  const dataRows = Math.max(complete.length - 1, 0);
  const rows = partial && sliceBytes > 0 ? Math.round((dataRows * totalBytes) / sliceBytes) : dataRows;
  const sample = columns.map((_, column) => complete.slice(1, 61).map((line) => splitLine(line, delimiter)[column] ?? ""));
  return { delimiter, columns, rows, approximate: partial, sample };
}

const readSlice = (file: Blob): Promise<ArrayBuffer> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(file);
  });

/** Reads at most {@link PEEK_BYTES} of the file; null when it does not look like a delimited table. */
export async function peekCsv(file: File): Promise<CsvPeek | null> {
  const lowered = file.name.toLowerCase();
  if (!/\.(csv|txt)$/.test(lowered)) return null;
  try {
    const buffer = await readSlice(file.slice(0, PEEK_BYTES));
    let text = new TextDecoder("utf-8").decode(buffer);
    // an export in a Windows code page: umlauts come out as replacement characters in UTF-8
    if (text.includes("�")) {
      try {
        text = new TextDecoder("windows-1252").decode(buffer);
      } catch {
        // keep the UTF-8 reading
      }
    }
    return peekText(text, file.size, buffer.byteLength);
  } catch {
    return null;
  }
}

const normalize = (value: string) =>
  value
    .toLowerCase()
    .replace(/ä/g, "ae")
    .replace(/ö/g, "oe")
    .replace(/ü/g, "ue")
    .replace(/ß/g, "ss")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

const ID_HEADER = /(^| )(id|ids|nr|nummer|number|no|kennzeichen|schluessel|key|kks|aks|guid|uuid|bezeichner|inventarnummer)( |$)/;
const NAME_HEADER = /(bezeichnung|benennung|name|beschreibung|text|anlage|asset|objekt)/;
const GENERIC_NAME = /^(anlage|asset|objekt|equipment|geraet|gerat)\s*[-_]?\s*\d+$/;

export interface AssetHints {
  /** No column looks like an id. */
  noId: boolean;
  /** Most names are only "Anlage 12". */
  genericNames: boolean;
}

/** Friendly hints for the asset list (slot 2); only ever a nudge. */
export function assetHints(peek: CsvPeek): AssetHints {
  const headers = peek.columns.map(normalize);
  const noId = !headers.some((header) => ID_HEADER.test(header));
  const nameColumns = headers.map((header, index) => (NAME_HEADER.test(header) ? index : -1)).filter((index) => index >= 0);
  let generic = 0;
  let total = 0;
  nameColumns.forEach((column) => {
    peek.sample[column].forEach((value) => {
      if (!value.trim()) return;
      total += 1;
      if (GENERIC_NAME.test(normalize(value))) generic += 1;
    });
  });
  return { noId, genericNames: total >= 3 && generic / total >= 0.5 };
}
